/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { Reducer } from 'redux';
import { MetadataActions } from '../actions/metadataActions';
import MetadataState from '../enums/MetadataState';
import QueryLimitMode from '../enums/QueryLimitMode'
import { DrillDownLevel, IState } from '../store/types/StateTypes';
import checkAvailableVisualizationTypes from './metadataReducerHelpers/checkAvailableVisualizationTypes';
import { IMetadataReducerActionData, IReducerAction } from '../store/types/ReducerTypes';
import { getLevelsLayout } from './metadataReducerHelpers/getLevelsLayout';
import { getTableUIComponent } from './metadataReducerHelpers/getTableUIComponent';
import { getCrosstabUIComponent } from './metadataReducerHelpers/getCrosstabUIComponent';
import { getCommonChartUIComponent } from './metadataReducerHelpers/getCommonChartUIComponent';
import { IMetadata, IMetadataComponent, MetadataQuery } from '../store/types/MetadataTypes';
import { getFilterExpression, getFilterParameters, updateFilterParameters } from './metadataReducerHelpers/filterHelper';
import showTitleReducer from './titleOptionsHelpers/showTitleReducer';

function getSelectedVisualizationFromMetadata(metadataObj: IMetadata, state: IState) {
    let uiComponent = metadataObj.component.components[0].componentType;

    if (uiComponent === 'chart') {
        uiComponent = metadataObj.component.components[0].properties.type.toLowerCase();
    }

    if (state.canvas && state.canvas.type) {
        uiComponent = state.canvas.type.toLowerCase();
    }

    const registryEntry = state.visualizationTypesRegistry.find((rItem) => (
        rItem.descriptor.name.toLowerCase() === uiComponent
    ));

    return registryEntry ? registryEntry.descriptor : state.visualizationDescriptor;
}

function convertPathItemsIntoClientSideFormat(order :any) {
    if (!order || !order.path) {
        return order;
    }

    return {
        ...order,
        path: order.path.map((pathItem :string) => pathItem.replace('All', 'total_node'))
    };
}

function getOrderInformation(metaObj: any) {
    let query = {} as MetadataQuery;
    let result;

    if (metaObj.multiAxis) {
        query = metaObj.multiAxis;
    } else if (metaObj.multiLevel) {
        query = metaObj.multiLevel;
    }

    const { orderBy = [] } = query;

    if (metaObj.multiAxis) {
        result = {
            topN: orderBy.filter((order: any) => (order.topN)).map((order: any) => (order.topN)).map(convertPathItemsIntoClientSideFormat),
            bottomN: orderBy.filter((order: any) => (order.bottomN)).map((order: any) => (order.bottomN)).map(convertPathItemsIntoClientSideFormat),
            members: orderBy.filter((order: any) => (order.member)).map((order: any) => (order.member)).map(convertPathItemsIntoClientSideFormat),
            levels: orderBy.filter((order: any) => (order.level)).map((order: any) => (order.level))
        };
    } else if (metaObj.multiLevel) {
        result = {
            topN: [],
            bottomN: [],
            members: [],
            levels: orderBy
        };
    } else {
        result = {
            topN: [],
            bottomN: [],
            members: [],
            levels: []
        };
    }

    return result;
}

function getDataLimit(component: IMetadataComponent, isDesignMode: boolean) {
    let result = -1;

    if (isDesignMode) {
        const {
            properties: {
                queryLimitMode
            }
        } = component;

        switch (queryLimitMode) {
        case QueryLimitMode.SAMPLE_DATA: result = 100; break;
        case QueryLimitMode.NO_DATA: result = 0; break;
        default: ;
        }
    }

    return result;
}

const metadataReducer: Reducer<any, IReducerAction<IMetadataReducerActionData>> = (state: IState, action: IReducerAction<IMetadataReducerActionData>) => {
    let newState = state;

    if (action.type === MetadataActions.METADATA_LOADED) {
        const actionData = action.data;
        if (!actionData) {
            throw new Error('Action data wasn\'t put');
        }

        if (actionData.metadataObj) {
            // conversion of the response to state happens here
            const levelsLayout = getLevelsLayout(actionData.metadataObj);
            const filterExpression = getFilterExpression(actionData.metadataObj.query);
            let filterParameters = getFilterParameters(actionData.metadataObj.query, actionData.metadataObj.schema);
            const orderInformation = getOrderInformation(actionData.metadataObj.query);
            const dataLimit = getDataLimit(actionData.metadataObj.component, state.isInDesignerMode);
            const drillDown: DrillDownLevel[] = [];

            if (state.params) {
                filterParameters = updateFilterParameters(filterParameters, state.params);
            }

            const uiComponents = [
                getTableUIComponent(actionData.metadataObj, levelsLayout),
                getCrosstabUIComponent(actionData.metadataObj, levelsLayout),
                getCommonChartUIComponent(actionData.metadataObj, levelsLayout),
            ];

            const metadataObj = {
                metadataState: MetadataState.LOADED,
                metadata: {
                    uiComponents
                },
                // here we simply apply the title properties which are assigned to the resource.
                // these two properties (title and showTitle) should always be present and have some values.
                titleOptions: {
                    ...state.titleOptions,
                    title: actionData.metadataObj.component.properties.title || '',
                    showTitleFlagFromMetadata: actionData.metadataObj.component.properties.showTitle
                },
                dataLimit,
                filterExpression,
                filterParameters,
                orderInformation,
                metadataObj: actionData.metadataObj, // TODO: need for legacy charts, make use of the corresponding component instead
                levelsLayout,
                drillDown
            };

            const visualizationDescriptor = getSelectedVisualizationFromMetadata(actionData.metadataObj, state);

            newState = {
                ...state,
                ...metadataObj,
                visualizationDescriptor,
                visualization: {
                    ...state.visualization,
                    ...actionData.metadataObj.visualization
                }
            };

            // based on the new props we can decide if we ned to show title
            newState.titleOptions.showTitle = showTitleReducer(newState);

            newState.visualizationTypesRegistry = checkAvailableVisualizationTypes(newState);

            const disabledTypes :string[] = [];
            newState.visualizationTypesRegistry.forEach((type) => {
                if (!type.available) {
                    disabledTypes.push(type.descriptor.name);
                }
            });
            newState.visualizationChooser.disabledTypes = disabledTypes;
        }
    }

    if (action.type === MetadataActions.METADATA_ERROR) {
        newState = {
            ...state,
            metadataState: MetadataState.ERROR,
            metadataError: action.errorDescriptor
        };
    }

    if (action.type === MetadataActions.METADATA_INVALID) {
        newState = {
            ...state,
            metadataState: MetadataState.INVALID,
            metadataError: action.errorDescriptor
        };
    }

    return newState;
};

export default metadataReducer;
